// El REACOMODO suave de una grilla: cuando una card se va (o entra, o el filtro cambia
// qué se ve), las demás no saltan a su lugar nuevo: se deslizan desde donde estaban
// (ADR-2026-09-12-las-cards-se-acomodan-y-el-chip-se-despliega).
//
// Es la técnica FLIP: en cada commit se miden las posiciones de las cards y se comparan
// con las del commit anterior; la que se movió arranca con un `translate` que la deja
// donde estaba y se anima hasta cero. Se hace en un layout effect, antes de pintar, así
// el primer frame ya la muestra en el lugar viejo. Las que aparecen se funden a la vista,
// pero sólo cuando alguna card quedó de antes: al cambiar de pestaña o en la primera
// carga no hay nada que "acomodar" y fundir sesenta cards sería un parpadeo.
//
// Las posiciones se miden RESPECTO DE LA GRILLA, no de la ventana ni de un ancestro:
// la grilla viaja con el panel cuando éste se abre empujando la conversación
// (ADR-2026-09-12-la-biblioteca-se-abre-y-se-cierra-empujando), y con `offsetLeft` —que
// es respecto del ancestro posicionado más cercano, fuera del panel— todas las cards
// "se movían" cientos de píxeles al terminar la apertura y en el render siguiente se
// deslizaban desde ahí: era la doble animación que se veía al abrir. La diferencia
// entre el rect de la card y el de la grilla no cambia con el panel ni con el scroll.

import { useLayoutEffect, useRef, type RefObject } from 'react';
import { prefiereMenosMovimiento } from './vueloAlComposer';

/** Cada card de la grilla lleva este atributo con su clave. */
export const ATRIBUTO_FICHA = 'data-ficha';

const DURACION_MS = 320;
const CURVA = 'cubic-bezier(.2,.7,.25,1)';

export function useReacomodoSuave(grillaRef: RefObject<HTMLElement>): void {
  const previasRef = useRef<Map<string, { left: number; top: number }>>(new Map());

  useLayoutEffect(() => {
    const grilla = grillaRef.current;
    const previas = previasRef.current;
    const actuales = new Map<string, { left: number; top: number }>();
    if (!grilla) {
      previasRef.current = actuales;
      return;
    }
    const cards = Array.from(grilla.querySelectorAll<HTMLElement>(`[${ATRIBUTO_FICHA}]`));
    const base = grilla.getBoundingClientRect();
    for (const el of cards) {
      const clave = el.getAttribute(ATRIBUTO_FICHA);
      if (!clave) continue;
      const r = el.getBoundingClientRect();
      actuales.set(clave, { left: r.left - base.left, top: r.top - base.top });
    }
    previasRef.current = actuales;
    if (prefiereMenosMovimiento() || previas.size === 0) return;

    let quedoAlguna = false;
    for (const clave of actuales.keys()) if (previas.has(clave)) quedoAlguna = true;
    if (!quedoAlguna) return;

    for (const el of cards) {
      if (typeof el.animate !== 'function') break;
      const clave = el.getAttribute(ATRIBUTO_FICHA);
      if (!clave) continue;
      const ahora = actuales.get(clave);
      const antes = previas.get(clave);
      if (!ahora) continue;
      if (!antes) {
        // Nueva (volvió de la caja): se funde a la vista.
        el.animate(
          [
            { opacity: 0, transform: 'scale(0.96)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: 220, easing: 'ease-out' },
        );
        continue;
      }
      const dx = antes.left - ahora.left;
      const dy = antes.top - ahora.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
      el.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
        { duration: DURACION_MS, easing: CURVA },
      );
    }
  });
}
